import { useEffect, useMemo, useRef, useState } from 'react'

import { EChart } from '@/components/charts/EChart'
import { Input } from '@/components/ui/input'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import {
  accuracyChartOption,
  categoryChartOption,
  DEFAULT_THRESHOLD_PCT,
  diffDirection,
  parseThreshold,
  signedDiff,
  summarizeVariance,
  type AccuracyMetric,
} from '@/features/cash/variance-view'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { formatDecimal } from '@/lib/format'
import { messages } from '@/lib/messages'
import type { CashVariance } from '@/services/http/cash'

const t = messages.cash.variance

function Stat({
  label,
  value,
  hint,
  testId,
}: {
  label: string
  value: string
  hint?: string
  testId: string
}) {
  return (
    <div className="bg-surface min-w-0 rounded-lg border p-3" title={hint}>
      <p className="text-text-secondary text-xs">{label}</p>
      <p className="mt-1 truncate text-sm font-semibold tabular-nums" data-testid={testId}>
        {value}
      </p>
    </div>
  )
}

/** Üstteki özet: "bu dönem toplam sapma X, en çok sapan kalem Y"; yanıttan türetilir. */
export function VarianceSummaryCards({
  variance,
  thresholdPct,
}: {
  variance: CashVariance
  thresholdPct: number
}) {
  const s = t.summary
  const summary = useMemo(
    () => summarizeVariance(variance.categories, variance.caris, thresholdPct),
    [variance.categories, variance.caris, thresholdPct],
  )
  const withDirection = (diff: number) =>
    s.deviationOf(signedDiff(diff), t.directions[diffDirection(diff)] ?? '')
  return (
    <section
      className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4"
      aria-label={s.label}
      data-testid="cash-variance-summary"
    >
      <Stat
        label={s.absoluteTotal}
        value={formatDecimal(summary.absoluteTotal, 0)}
        hint={s.absoluteTotalHint}
        testId="cash-variance-total"
      />
      <Stat
        label={s.topCategory}
        value={
          summary.topCategory
            ? `${t.kinds[summary.topCategory.key] ?? summary.topCategory.key}: ${withDirection(summary.topCategory.diff)}`
            : s.none
        }
        testId="cash-variance-top-category"
      />
      <Stat
        label={s.topCari}
        value={
          summary.topCari
            ? `${summary.topCari.name ?? summary.topCari.code}: ${withDirection(summary.topCari.diff)}`
            : s.none
        }
        testId="cash-variance-top-cari"
      />
      <Stat
        label={s.exceeding}
        value={s.exceedingValue(summary.exceeding, summary.categoryCount)}
        hint={`${t.threshold.label}: ${formatDecimal(thresholdPct, 0)}`}
        testId="cash-variance-exceeding"
      />
    </section>
  )
}

/** Vurgu eşiği: görünüm tercihi, URL'de (`esik`); yazarken gecikmeli ve `replace` ile yazılır. */
export function ThresholdInput({
  value,
  onChange,
}: {
  value: number
  onChange: (next: number) => void
}) {
  const [text, setText] = useState(() => String(value))
  const debounced = useDebouncedValue(text, 300)
  const pushed = useRef(value)
  useEffect(() => {
    const next = parseThreshold(debounced)
    if (debounced.trim() === '' || next === pushed.current) return
    pushed.current = next
    onChange(next)
  }, [debounced, onChange])
  return (
    <label className="text-text-secondary flex items-center gap-2 text-xs" title={t.threshold.hint}>
      {t.threshold.label}
      <Input
        className="w-20"
        inputMode="decimal"
        aria-label={t.threshold.label}
        placeholder={String(DEFAULT_THRESHOLD_PCT)}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
    </label>
  )
}

export function CategoryChart({ categories }: { categories: CashVariance['categories'] }) {
  const option = useMemo(() => categoryChartOption(categories), [categories])
  return (
    <section className="bg-surface rounded-lg border p-3" aria-label={t.charts.category}>
      <h2 className="mb-2 text-sm font-semibold">{t.charts.category}</h2>
      <EChart option={option} height={260} />
    </section>
  )
}

export function AccuracyTrend({
  accuracy,
  metric,
  onMetricChange,
}: {
  accuracy: CashVariance['accuracy']
  metric: AccuracyMetric
  onMetricChange: (next: AccuracyMetric) => void
}) {
  const option = useMemo(() => accuracyChartOption(accuracy, metric), [accuracy, metric])
  return (
    <section className="bg-surface rounded-lg border p-3" aria-label={t.charts.accuracy}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">{t.charts.accuracy}</h2>
        <ToggleGroup
          variant="outline"
          size="sm"
          aria-label={t.charts.metricLabel}
          value={[metric]}
          onValueChange={([next]) => {
            if (next === 'mape' || next === 'bias') onMetricChange(next)
          }}
        >
          <ToggleGroupItem value="mape">{t.charts.mape}</ToggleGroupItem>
          <ToggleGroupItem value="bias">{t.charts.bias}</ToggleGroupItem>
        </ToggleGroup>
      </div>
      <EChart option={option} height={260} />
      <p className="text-text-secondary mt-1 text-xs">{t.charts.accuracyHint}</p>
    </section>
  )
}
